import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, TYPE_META, TYPE_ORDER } from "@/lib/api";
import ItemCard from "@/components/ItemCard";
import Carousel from "@/components/Carousel";
import { motion } from "framer-motion";
import { Flourish, Fleuron } from "@/components/Ornaments";
import { ArrowLeft, Trash2, Share2, Copy, Check, X, Pencil, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import CollectionPhotos from "@/components/CollectionPhotos";

export default function CollectionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRename, setShowRename] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [colsRes, itemsRes] = await Promise.all([
          api.get("/collections"),
          api.get("/items", { params: { collection_id: id } }),
        ]);
        if (cancel) return;
        const found = (colsRes.data || []).find(c => c.id === id);
        if (!found) {
          toast.error("Collection not found");
          navigate("/collections", { replace: true });
          return;
        }
        setCollection(found);
        setItems(itemsRes.data || []);
      } catch (e) {
        console.error("[collection-view] load failed:", e);
        if (!cancel) navigate("/collections", { replace: true });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id, navigate]);

  async function removeCollection() {
    if (!collection) return;
    try {
      await api.delete(`/collections/${collection.id}`);
      toast.success("Collection removed");
      navigate("/collections");
    } catch { toast.error("Could not remove"); }
  }

  async function shareCollection() {
    if (!collection) return;
    try {
      let token = collection.share_token;
      if (!token) {
        const r = await api.post(`/collections/${collection.id}/share`);
        token = r.data.share_token;
        setCollection({ ...collection, share_token: token, share_enabled: true });
      }
      const url = `${window.location.origin}/c/${token}`;
      setShareUrl(url);
      try {
        setCopying(true);
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      } catch {
        toast("Share link ready — copy it below");
      } finally { setCopying(false); }
    } catch { toast.error("Could not create share link"); }
  }

  async function stopSharing() {
    if (!collection) return;
    try {
      await api.delete(`/collections/${collection.id}/share`);
      setCollection({ ...collection, share_token: null, share_enabled: false });
      setShareUrl("");
      toast("Sharing stopped");
    } catch { toast.error("Could not stop sharing"); }
  }

  async function toggleVisibility() {
    if (!collection) return;
    const next = collection.visibility === "public" ? "private" : "public";
    try {
      const r = await api.patch(`/collections/${collection.id}`, { visibility: next });
      setCollection({ ...collection, ...r.data });
      toast.success(next === "public"
        ? "now visible on your profile"
        : "now private — only you can see this");
    } catch (e) {
      console.error("[collection:visibility] failed:", e);
      toast.error("could not update visibility");
    }
  }

  if (loading || !collection) {
    return (
      <p className="font-cinzel text-xs tracking-[0.26em] uppercase text-[var(--ink-dusty)]">
        Opening the collection…
      </p>
    );
  }

  // Group by type
  const byType = {};
  for (const it of items) (byType[it.type] ||= []).push(it);

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-14" data-testid="collection-view">
      <button
        onClick={() => navigate("/collections")}
        className="flex items-center gap-2 font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)] hover:text-[var(--bronze)]"
        data-testid="back-to-collections-btn"
      >
        <ArrowLeft size={12}/> All collections
      </button>

      <header className="text-center">
        <Fleuron size={26} color="var(--bronze)" className="mx-auto opacity-70" />
        <span className="stamp mt-4 inline-block" data-testid="collection-stamp" style={{ "--stamp-color": "var(--bronze)" }}>
          Collection · {items.length} item{items.length === 1 ? "" : "s"}
        </span>
        <h1 className="mt-5 font-serif-lit text-5xl sm:text-6xl tracking-tight text-[var(--ink)] leading-[1.05]">
          {collection.name}
        </h1>
        {collection.description && (
          <p className="mt-4 font-garamond italic text-lg text-[var(--ink-dusty)] max-w-xl mx-auto">
            {collection.description}
          </p>
        )}
        <Flourish color="var(--bronze)" className="mx-auto mt-6 opacity-80" />

        {/* Collection actions live directly below the header so they stay
         *  reachable no matter how tall the shelves grow underneath. */}
        <div className="mt-8 flex flex-wrap justify-center gap-2 sm:gap-3" data-testid="collection-actions">
          <button
            onClick={toggleVisibility}
            data-testid="collection-visibility-btn"
            className="lc-btn-ghost inline-flex items-center gap-2"
            title={collection.visibility === "public" ? "make private" : "make public"}
          >
            {collection.visibility === "public"
              ? (<><Globe size={14} strokeWidth={1.6}/> public — tap to hide</>)
              : (<><Lock size={14} strokeWidth={1.6}/> private — tap to share</>)}
          </button>
          <button
            onClick={shareCollection}
            className="lc-btn-ghost inline-flex items-center gap-2"
            data-testid="share-collection-btn"
          >
            <Share2 size={14} strokeWidth={1.4}/> Share
          </button>
          <button
            onClick={() => setShowRename(true)}
            className="lc-btn-ghost inline-flex items-center gap-2"
            data-testid="edit-collection-btn"
          >
            <Pencil size={14} strokeWidth={1.4}/> Edit
          </button>
          {confirmDel ? (
            <span className="inline-flex items-center gap-2" data-testid="delete-collection-confirm">
              <span className="font-garamond italic text-sm text-[var(--terracotta)]">Delete?</span>
              <button onClick={removeCollection} className="lc-btn-ghost text-[var(--terracotta)] border-[rgba(158,59,43,0.45)] text-xs" data-testid="delete-collection-confirm-yes">
                Yes, remove
              </button>
              <button onClick={() => setConfirmDel(false)} className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-[var(--ink-dusty)] hover:text-[var(--ink)]" data-testid="delete-collection-confirm-cancel">
                Cancel
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirmDel(true)}
              className="lc-btn-ghost inline-flex items-center gap-2 text-[var(--terracotta)] border-[rgba(158,59,43,0.4)]"
              data-testid="delete-collection-btn"
            >
              <Trash2 size={14} strokeWidth={1.4}/> Remove
            </button>
          )}
        </div>
      </header>

      {/* Collection photos (gallery + cover management) — lives above the
          item shelves so visitors see the mood of the collection first,
          then the contents underneath. */}
      <CollectionPhotos
        collection={collection}
        onChange={(patch) => setCollection(prev => ({ ...prev, ...patch }))}
      />


      {items.length === 0 ? (
        <div className="max-w-xl mx-auto ornate-frame text-center" data-testid="collection-empty">
          <Fleuron size={22} color="var(--bronze)" className="mx-auto opacity-70" />
          <p className="mt-3 font-serif-lit text-2xl italic text-[var(--ink)]">An empty collection.</p>
          <p className="mt-3 font-garamond italic text-[var(--ink-dusty)]">
            Open any item from your library and toggle this collection under <em>Add to collection</em>.
          </p>
        </div>
      ) : (
        <div className="space-y-14">
          {TYPE_ORDER.concat(["unknown"]).map(type => {
            const list = byType[type];
            if (!list || list.length === 0) return null;
            const meta = TYPE_META[type] || TYPE_META.unknown;
            return (
              <section
                key={type}
                className="shelf-section"
                style={{ "--shelf-bg": `${meta.bg}55`, "--shelf-border": meta.border }}
                data-testid={`collection-shelf-${type}`}
              >
                <header className="text-center mb-8">
                  <h2 className="font-cinzel text-xl tracking-[0.22em]" style={{ color: meta.accent }}>
                    {meta.label}
                  </h2>
                  <span className="mt-2 inline-block font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
                    {list.length} kept
                  </span>
                </header>
                {type === "picture" ? (
                  <div className="gallery-wall" data-testid={`collection-gallery-${type}`}>
                    {list.map((it, idx) => <ItemCard key={it.id} item={it} index={idx} variant="gallery"/>)}
                  </div>
                ) : type === "music" ? (
                  <Carousel accent={meta.accent} testId={`collection-carousel-${type}`} itemWidth={210}>
                    {list.map((it, idx) => (
                      <div key={it.id} className="vinyl-crate vinyl-crate-single">
                        <ItemCard item={it} index={idx} />
                      </div>
                    ))}
                  </Carousel>
                ) : (
                  <Carousel
                    accent={meta.accent}
                    testId={`collection-carousel-${type}`}
                    itemWidth={type === "video" ? 220 : type === "article" || type === "recipe" ? 240 : 170}
                  >
                    {list.map((it, idx) => <ItemCard key={it.id} item={it} index={idx} />)}
                  </Carousel>
                )}
              </section>
            );
          })}
        </div>
      )}

      {shareUrl && (
        <div className="placed-card p-5 max-w-2xl mx-auto" data-testid="share-card" style={{ borderColor: "var(--sage)" }}>
          <div className="flex items-center gap-3 mb-3">
            <Share2 size={14} className="text-[var(--sage)]" strokeWidth={1.4}/>
            <p className="font-cinzel text-[11px] tracking-[0.26em] uppercase" style={{ color: "var(--sage)" }}>
              Public read-only link
            </p>
          </div>
          <div className="flex items-stretch gap-2">
            <input readOnly value={shareUrl} data-testid="share-url-input"
                   className="lc-input flex-1 text-xs font-mono-lc"
                   onFocus={(e) => e.target.select()}/>
            <button onClick={async () => {
              try { await navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }
              catch { toast.error("Could not copy"); }
            }}
              className="lc-btn-ghost flex items-center gap-2" data-testid="share-copy-btn" disabled={copying}>
              {copying ? <Check size={13}/> : <Copy size={13}/>} Copy
            </button>
          </div>
          <button onClick={stopSharing} className="mt-3 font-cinzel text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)] hover:text-[var(--terracotta)]" data-testid="share-stop-btn">
            Stop sharing
          </button>
        </div>
      )}

      {showRename && (
        <RenameCollectionModal
          collection={collection}
          onClose={() => setShowRename(false)}
          onSaved={(updated) => { setCollection({ ...collection, ...updated }); setShowRename(false); }}
        />
      )}
    </motion.div>
  );
}

function RenameCollectionModal({ collection, onClose, onSaved }) {
  const [name, setName] = useState(collection.name || "");
  const [description, setDescription] = useState(collection.description || "");
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Give this collection a name"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/collections/${collection.id}`, {
        name: trimmed,
        description: description.trim(),
      });
      onSaved(r.data);
      toast.success("Collection updated");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save";
      toast.error(typeof msg === "string" ? msg : "Could not save");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-40 bg-[rgba(42,30,20,0.55)] flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()}
            className="bg-[var(--card-bg)] border border-[rgba(140,106,47,0.32)] rounded-md max-w-md w-full p-7 space-y-5"
            data-testid="rename-collection-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Edit collection</h2>
          <button type="button" onClick={onClose} data-testid="rename-close" aria-label="Close">
            <X size={16} className="text-[var(--ink-dusty)]"/>
          </button>
        </div>
        <div>
          <label className="font-cinzel text-[10px] tracking-[0.24em] uppercase text-[var(--ink-dusty)]">Name</label>
          <input
            data-testid="rename-name-input"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={120}
            className="lc-input mt-2 font-serif-lit text-[18px]"
            autoFocus
          />
        </div>
        <div>
          <label className="font-cinzel text-[10px] tracking-[0.24em] uppercase text-[var(--ink-dusty)]">Description (optional)</label>
          <textarea
            data-testid="rename-description-input"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
            className="lc-input mt-2 font-garamond text-[15px] leading-relaxed"
            placeholder="One line about what belongs here…"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="lc-btn-ghost" data-testid="rename-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="lc-btn" data-testid="rename-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
